import { useEffect, useState } from "react";
import { Plus, Trash2, Save, AlertTriangle } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TYPES = ["market", "property_type", "opportunity_category", "investment_category", "lead_source", "pipeline_stage", "tag", "business_info"];

export default function AdminSettings() {
  const [type, setType] = useState("market");
  const [items, setItems] = useState([]);
  const [n, setN] = useState({ key: "", label: "", order: 0 });
  const [dangerOpen, setDangerOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/settings/taxonomy/${type}`); setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const add = async () => {
    if (!n.key || !n.label) return;
    try { await api.post("/settings/taxonomy", { type, key: n.key.toLowerCase(), label: n.label, order: parseInt(n.order || 0) });
      setN({ key: "", label: "", order: 0 }); load(); toast.success("Added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const update = async (id, patch) => { await api.patch(`/settings/taxonomy/${id}`, patch); load(); };
  const del = async (id) => { if (!window.confirm("Delete this entry?")) return; await api.delete(`/settings/taxonomy/${id}`); load(); };

  const clearDemo = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      const { data } = await api.post("/admin/clear-demo-data", { confirm: confirmText });
      const c = data.cleared || {};
      toast.success(`Demo data cleared: ${c.leads || 0} leads, ${c.consultations || 0} consultations, ${c.tasks || 0} tasks, ${c.opportunities_sample || 0} sample opps.`);
      setDangerOpen(false); setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear demo data");
    } finally { setClearing(false); }
  };

  return (
    <div data-testid="page-admin-settings">
      <AdminHeader breadcrumb="Configuration" title="Settings" subtitle="Manage every taxonomy used across Altura Global — markets, pipeline stages, sources, tags, and more." />
      <div className="p-8 space-y-6">
        <div className="admin-card flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)} className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${type === t ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                    data-testid={`settings-tab-${t}`}>{t.replace(/_/g, " ")}</button>
          ))}
        </div>

        <div className="admin-card">
          <div className="grid grid-cols-4 gap-3 items-end">
            <div><div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Key</div><input className="admin-input mt-1" value={n.key} onChange={(e) => setN({ ...n, key: e.target.value })} placeholder="snake_case_key" data-testid="settings-new-key" /></div>
            <div><div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Label</div><input className="admin-input mt-1" value={n.label} onChange={(e) => setN({ ...n, label: e.target.value })} data-testid="settings-new-label" /></div>
            <div><div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60">Order</div><input type="number" className="admin-input mt-1" value={n.order} onChange={(e) => setN({ ...n, order: e.target.value })} data-testid="settings-new-order" /></div>
            <button onClick={add} className="btn-gold" data-testid="settings-add"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
        </div>

        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.15em] text-altura-charcoal/50">
              <tr className="border-b border-altura-espresso/10"><th className="text-left py-3 px-2">Key</th><th className="text-left py-3 px-2">Label</th><th className="text-left py-3 px-2">Order</th><th className="text-left py-3 px-2">Active</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-b border-altura-espresso/5">
                  <td className="py-3 px-2 font-mono text-xs">{i.key}</td>
                  <td className="py-3 px-2"><input defaultValue={i.label} className="admin-input" onBlur={(e) => e.target.value !== i.label && update(i.id, { label: e.target.value })} data-testid={`settings-label-${i.id}`} /></td>
                  <td className="py-3 px-2 w-24"><input type="number" defaultValue={i.order} className="admin-input" onBlur={(e) => parseInt(e.target.value) !== i.order && update(i.id, { order: parseInt(e.target.value) })} data-testid={`settings-order-${i.id}`} /></td>
                  <td className="py-3 px-2"><input type="checkbox" defaultChecked={i.active} onChange={(e) => update(i.id, { active: e.target.checked })} data-testid={`settings-active-${i.id}`} /></td>
                  <td className="py-3 px-2"><button onClick={() => del(i.id)} className="text-altura-espresso/60 hover:text-altura-burgundy" data-testid={`settings-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-altura-charcoal/50">No entries.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* DANGER ZONE */}
        <div className="rounded-md border border-altura-burgundy/40 bg-altura-burgundy/5 p-5 mt-10" data-testid="danger-zone">
          <div className="flex items-start gap-4">
            <div className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-altura-burgundy/15 text-altura-burgundy">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="font-editorial text-xl text-altura-espresso">Danger Zone — Production Preparation</div>
              <p className="mt-2 text-sm text-altura-charcoal/75">
                Remove every demonstration record from this instance in preparation for a production launch.
                This clears <strong>all leads, lead activities, tasks, consultations, form submissions, partner applications,</strong> and <strong>every seeded sample opportunity</strong> (any opportunity whose Internal Notes starts with <code>[SAMPLE DATA</code>).
                Your users, taxonomies/settings, and non-sample opportunities are preserved.
              </p>
              {!dangerOpen ? (
                <button onClick={() => setDangerOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-altura-burgundy px-5 py-2 text-xs uppercase tracking-widest text-altura-burgundy hover:bg-altura-burgundy hover:text-altura-ivory transition" data-testid="danger-open">
                  <AlertTriangle className="h-3.5 w-3.5" /> Clear Demo Data
                </button>
              ) : (
                <div className="mt-5 space-y-3">
                  <p className="text-sm text-altura-espresso">
                    To confirm, type <code className="bg-altura-burgundy/15 text-altura-burgundy font-mono px-2 py-0.5 rounded">CLEAR DEMO DATA</code> exactly:
                  </p>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                         placeholder="CLEAR DEMO DATA"
                         className="admin-input max-w-md" data-testid="danger-confirm-input" />
                  <div className="flex flex-wrap gap-2">
                    <button onClick={clearDemo} disabled={clearing || confirmText.trim() !== "CLEAR DEMO DATA"}
                            className="inline-flex items-center gap-2 rounded-full bg-altura-burgundy px-5 py-2.5 text-xs uppercase tracking-widest text-altura-ivory hover:bg-altura-burgundy/85 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            data-testid="danger-confirm-submit">
                      {clearing ? "Clearing…" : "Confirm & Clear"}
                    </button>
                    <button onClick={() => { setDangerOpen(false); setConfirmText(""); }} className="btn-ghost-espresso" data-testid="danger-cancel">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
